import { Link, useNavigate, useLocation } from "react-router-dom";
import { encodeId } from "./HelperMethods";
import { useState, useEffect } from "react";
import { authFetch, isAuthenticated } from "../utils/authUtils";
import StarRating from "./Common/StarRating";
import "./Common/StarRating.css";

interface SongListProps {
  song: {
    song_id: string;
    melon_album_id: string;
    artist_names: string[];
    english_name: string;
    track_order: number;
    is_title: boolean;
    is_explicit: boolean;
  };
}

const SongList: React.FC<SongListProps> = ({ song }) => {
  const song_id = song.song_id;
  const album_id = song.melon_album_id;
  const [userRating, setUserRating] = useState<number>(0);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  let names = "";
  song.artist_names.forEach((name: string) => (names += name + ", "));
  names = names.substring(0, names.length - 2);
  // var name = song.english_name
  //   .toLowerCase()
  //   .replaceAll(' ', '-')
  //   .replaceAll(/"([^"]+(?="))"/g, '')
  //   .replaceAll("'", '')
  //   .replaceAll('/', '-'); // TODO simplify this

  // Check if user is logged in
  useEffect(() => {
    setIsLoggedIn(isAuthenticated());
  }, []);

  // Fetch user's rating for this song
  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchUserRating = async () => {
      try {
        const response = await fetch(`/api/reviews/song/${song_id}`);
        if (response.ok) {
          const reviews = await response.json();

          // Get user data from local storage
          const userData = localStorage.getItem('user');
          if (!userData) return;

          const user = JSON.parse(userData);
          const userId = user.userId || user.id || user._id;

          // Find the user's review
          const userReview = reviews.find((review: any) =>
            review.user_id === userId || review.is_owner === true
          );

          if (userReview) {
            setUserRating(userReview.rating);
          }
        }
      } catch (error) {
        console.error("Error fetching user rating:", error);
      }
    };

    fetchUserRating();
  }, [song_id, isLoggedIn]);

  // Handle rating change
  const handleRatingChange = async (rating: number) => {
    if (!isLoggedIn || isSubmitting) return;

    setIsSubmitting(true);

    // Update UI immediately for better user experience
    setUserRating(rating);

    try {
      // Check if user already has a review for this song
      const response = await fetch(`/api/reviews/song/${song_id}`);
      const reviews = await response.json();

      const userData = localStorage.getItem('user');
      if (!userData) {
        setIsSubmitting(false);
        return;
      }

      const user = JSON.parse(userData);
      const userId = user.userId || user.id || user._id;

      const existingReview = reviews.find((review: any) =>
        review.user_id === userId || review.is_owner === true
      );

      if (existingReview) {
        // Update existing review
        await authFetch(`/api/reviews/${existingReview.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rating: rating,
            content: existingReview.content || " "
          })
        });
      } else {
        // Create new review
        await authFetch("/api/reviews", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            song_id: song_id,
            rating: rating,
            content: " "
          })
        });
      }

      // Notify other components that a review was updated
      window.dispatchEvent(new CustomEvent('reviewUpdated'));

    } catch (error) {
      console.error("Error updating rating:", error);
      // Revert to previous rating if there was an error
      setUserRating(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect to login page when non-logged-in user clicks on stars
  const handleLoginRedirect = () => {
    // Store current location to return after login
    localStorage.setItem('returnToUrl', location.pathname + location.search);
    navigate('/login');
  };

  return (
    <div className="song-list">
      <div className="track-left">
        {song.track_order}.{" "}

        <Link
          to={`/song/${encodeId(song_id)}`}
          state={{ song_id: song_id, album_id: album_id }}
        >
          {song.is_title && "✰ "}
          {song.is_explicit && "🅴 "}
          {song.english_name}
        </Link>
      </div>
      <div className="song-list-right">
        {isLoggedIn ? (
          <div className="quick-rating">
            <StarRating
              rating={userRating}
              interactive={true}
              onRatingChange={handleRatingChange}
              size="small"
              className="song-list-rating"
            />
          </div>
        ) : (
          <div className="quick-rating login-redirect" onClick={handleLoginRedirect}>
            <StarRating
              rating={0}
              interactive={true}
              onRatingChange={handleLoginRedirect}
              size="small"
              className="song-list-rating"
            />
          </div>
        )}
        <span className="by-song-artist">{names}</span>
      </div>
    </div>
  );
};

export default SongList;
